import { arrayMove } from "@dnd-kit/sortable";

/**
 * Tree helpers for the menu builder.
 *
 * The builder works on a FLAT array of nodes (one row each) while dragging,
 * which is what `@dnd-kit/sortable` understands. Each flat node carries the
 * domain fields the backend expects (MenuNavItem_Name, MenuNavItem_Url, ...)
 * plus three transient fields used only for rendering/drag:
 *   - id       string  (dnd-kit id, mirrors Id)
 *   - parentId string | null
 *   - depth    number  (0 = top level)
 *
 * On save we rebuild the nested `menunavstructure_list` shape the API uses and
 * recompute Level / Order / ParentId from the tree position.
 */

// How many levels the UI allows. depth is 0-based, so MAX_LEVELS = 3 means
// depth 0,1,2 -> Level "1","2","3". Bump this once the backend persists deeper
// levels (today it has historically only stored Level "1" and "2").
export const MAX_LEVELS = 3;
const MAX_DEPTH = MAX_LEVELS - 1;

// Horizontal pixels of indentation per nesting level.
export const INDENTATION_WIDTH = 28;

function stripTransient(item) {
  // remove fields that should never be persisted / re-derived on build
  const { children, parentId, depth, index, id, menunavstructure_list, ...rest } =
    item;
  return rest;
}

/** Nested API tree -> flat list with depth/parentId. */
export function flattenTree(items, parentId = null, depth = 0) {
  return (items || []).reduce((acc, item, index) => {
    const node = {
      ...stripTransient(item),
      Id: String(item.Id),
      id: String(item.Id),
      parentId,
      depth,
      index,
    };
    const children = item.menunavstructure_list || [];
    return [
      ...acc,
      node,
      ...flattenTree(children, String(item.Id), depth + 1),
    ];
  }, []);
}

function hasChildren(items, id) {
  return items.some((item) => item.parentId === id);
}

/** Hide descendants of collapsed (or currently dragged) parents. */
export function removeChildrenOf(items, ids) {
  const excludeParentIds = [...ids];
  return items.filter((item) => {
    if (item.parentId && excludeParentIds.includes(item.parentId)) {
      if (hasChildren(items, item.id)) excludeParentIds.push(item.id);
      return false;
    }
    return true;
  });
}

export function getChildCount(items, id) {
  return items.filter((item) => item.parentId === id).length;
}

/** Depth of the deepest descendant relative to `id` (0 if it has no children). */
export function getSubtreeHeight(items, id) {
  const node = items.find((i) => i.id === id);
  if (!node) return 0;
  const descendantIds = new Set([id]);
  let changed = true;
  while (changed) {
    changed = false;
    for (const it of items) {
      if (it.parentId && descendantIds.has(it.parentId) && !descendantIds.has(it.id)) {
        descendantIds.add(it.id);
        changed = true;
      }
    }
  }
  let maxDepth = node.depth;
  for (const it of items) {
    if (descendantIds.has(it.id) && it.depth > maxDepth) maxDepth = it.depth;
  }
  return maxDepth - node.depth;
}

function getMaxDepth(previousItem) {
  return previousItem ? previousItem.depth + 1 : 0;
}

function getMinDepth(nextItem) {
  return nextItem ? nextItem.depth : 0;
}

/**
 * Given a drag in progress, work out the depth + parent the dragged row would
 * land at. This is the standard dnd-kit "sortable tree" projection, clamped to
 * MAX_DEPTH.
 */
export function getProjection(
  items,
  activeId,
  overId,
  dragOffset,
  indentationWidth = INDENTATION_WIDTH,
  maxDepthCap = MAX_DEPTH
) {
  const overItemIndex = items.findIndex(({ id }) => id === overId);
  const activeItemIndex = items.findIndex(({ id }) => id === activeId);
  const activeItem = items[activeItemIndex];
  if (!activeItem || overItemIndex === -1) return null;

  const newItems = arrayMove(items, activeItemIndex, overItemIndex);
  const previousItem = newItems[overItemIndex - 1];
  const nextItem = newItems[overItemIndex + 1];

  const dragDepth = Math.round(dragOffset / indentationWidth);
  const projectedDepth = activeItem.depth + dragDepth;

  const maxDepth = Math.min(getMaxDepth(previousItem), maxDepthCap);
  const minDepth = getMinDepth(nextItem);

  let depth = projectedDepth;
  if (projectedDepth >= maxDepth) depth = maxDepth;
  else if (projectedDepth < minDepth) depth = minDepth;

  return { depth, maxDepth, minDepth, parentId: getParentId() };

  function getParentId() {
    if (depth === 0 || !previousItem) return null;
    if (depth === previousItem.depth) return previousItem.parentId;
    if (depth > previousItem.depth) return previousItem.id;
    const newParent = newItems
      .slice(0, overItemIndex)
      .reverse()
      .find((item) => item.depth === depth)?.parentId;
    return newParent ?? null;
  }
}

/**
 * Flat list (in visual order) -> nested API shape with Level/Order/ParentId
 * recomputed. Used both to persist and to render the live preview.
 */
export function buildTree(flatItems) {
  const root = { id: "root", children: [] };
  const nodes = { root };
  const items = flatItems.map((item) => ({ ...item, children: [] }));

  for (const item of items) {
    const parentId = item.parentId ?? "root";
    const parent = nodes[parentId] || items.find((i) => i.id === parentId) || root;
    nodes[item.id] = item;
    parent.children = parent.children || [];
    parent.children.push(item);
  }

  return cleanup(root.children, 0, null);
}

function cleanup(children, depth, parentId) {
  return children.map((node, idx) => {
    const domain = stripTransient(node);
    return {
      ...domain,
      Id: String(node.id),
      Level: String(depth + 1),
      Order: String(idx + 1),
      ParentId: parentId,
      menunavstructure_list: cleanup(node.children || [], depth + 1, String(node.id)),
    };
  });
}

/** Turn an "available" placeholder item into a fresh top-level flat node. */
export function availableItemToFlatNode(item) {
  return {
    Id: String(item.Id),
    id: String(item.Id),
    MenuNavItem_Icon: item.Icon,
    MenuNavItem_Name: item.Name,
    MenuNavItem_Url: item.Url,
    MenuNavItem_Id: item.MenuNavOption_Id,
    Level: "1",
    Order: "1",
    ParentId: null,
    parentId: null,
    depth: 0,
    index: 0,
  };
}

/** Stable compare so we can tell whether the tree has unsaved changes. */
export function isTreeDirty(a, b) {
  const norm = (list) =>
    JSON.stringify(
      (list || []).map(({ id, Id, parentId, depth }) => ({
        id: String(id ?? Id),
        parentId: parentId ?? null,
        depth,
      }))
    );
  return norm(a) !== norm(b);
}
